/**
 * Pure peak-strip waveform computation for a decoded audio buffer — no
 * `AudioContext`/`Canvas` dependency, so it's unit-testable without a
 * browser. `AudioViewer.tsx` decodes the baked OGG via WebAudio, calls
 * `computePeaks` on the result, and draws it with `drawPeaks`.
 */

export interface WaveformPeaks {
  readonly mins: Float32Array;
  readonly maxes: Float32Array;
}

/** Downsamples `channelData` into `bucketCount` (min, max) pairs — one pair per horizontal pixel column of a peak-strip waveform. */
export function computePeaks(channelData: Float32Array, bucketCount: number): WaveformPeaks {
  if (bucketCount <= 0 || channelData.length === 0) {
    return { mins: new Float32Array(0), maxes: new Float32Array(0) };
  }

  const mins = new Float32Array(bucketCount);
  const maxes = new Float32Array(bucketCount);
  const samplesPerBucket = channelData.length / bucketCount;

  for (let i = 0; i < bucketCount; i++) {
    const start = Math.floor(i * samplesPerBucket);
    const end = Math.max(start + 1, Math.floor((i + 1) * samplesPerBucket));
    let min = 0;
    let max = 0;
    for (let j = start; j < end && j < channelData.length; j++) {
      const value = channelData[j]!;
      if (value < min) min = value;
      if (value > max) max = value;
    }
    mins[i] = min;
    maxes[i] = max;
  }

  return { mins, maxes };
}

/** Draws a min/max peak strip onto a 2D canvas context, one filled column per bucket. */
export function drawPeaks(ctx: CanvasRenderingContext2D, peaks: WaveformPeaks, width: number, height: number, color: string): void {
  ctx.clearRect(0, 0, width, height);
  if (peaks.maxes.length === 0) return;

  ctx.fillStyle = color;
  const mid = height / 2;
  const columnWidth = Math.max(1, width / peaks.maxes.length);
  for (let i = 0; i < peaks.maxes.length; i++) {
    const top = mid - peaks.maxes[i]! * mid;
    const bottom = mid - peaks.mins[i]! * mid;
    ctx.fillRect(i * columnWidth, top, columnWidth, Math.max(1, bottom - top));
  }
}
